import { FormAutoCompleteProps, FormAutocomplete, TranslationFuncType } from "@itk/mui-components";
import { OmitSafe } from "@itk/typescript-utils";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { RootState } from "src/store";
import { actions } from "./form-list-item-picture-autocomplete-store";

type ListItemPictureOption = { value: string; description: string; picture: string };

const mapStateToProps = (state: RootState) => ({ listItemsState: state.generalReducer.formListItemPictureAutocompleteReducer.listItemsState });

type OwnProps<TObject extends TranslationFuncType, TObjectKey extends keyof TObject> = OmitSafe<
  FormAutoCompleteProps<ListItemPictureOption, string, TObject, TObjectKey>,
  "options" | "getId" | "getOptionLabel" | "renderOption"
>;

const ownActions = { getListItemsRequest: actions.getListItemsRequest };

type Props<TObject extends TranslationFuncType, TObjectKey extends keyof TObject> = OwnProps<TObject, TObjectKey> & typeof ownActions & ReturnType<typeof mapStateToProps> & { listId: string };

const FormListItemPictureAutocompleteBase = <TObject extends TranslationFuncType, TObjectKey extends keyof TObject>(props: Props<TObject, TObjectKey>) => {
  const { getListItemsRequest, listItemsState, listId, ...rest } = props;
  const theme = useTheme();
  const currentListItemsState = listItemsState[listId];

  useEffect(() => {
    getListItemsRequest(listId);
  }, [listId]);

  const options = useMemo((): ListItemPictureOption[] => {
    return currentListItemsState?.loading === "has-loaded"
      ? currentListItemsState.data.map(d => ({ value: d.listItemId, description: d.value, picture: d.picture })).sort((a, b) => a.description.localeCompare(b.description))
      : [];
  }, [currentListItemsState]);

  return (
    <FormAutocomplete
      options={options}
      getId={option => option.value}
      getOptionLabel={option => option.description}
      renderOption={(liProps, option) => (
        <Box component="li" {...liProps} key={option.value}>
          <Stack direction="row" spacing={1} alignItems="center">
            {option.picture ? (
              <Box
                component="img"
                src={option.picture}
                alt=""
                sx={{
                  width: theme.spacing(5),
                  height: theme.spacing(5),
                  borderRadius: theme.shape.borderRadius,
                  borderStyle: "solid",
                  borderWidth: 1,
                  borderColor: theme.palette.customColors.border,
                  objectFit: "cover",
                  flexShrink: 0
                }}
              />
            ) : null}
            <Typography variant="body1">{option.description}</Typography>
          </Stack>
        </Box>
      )}
      {...rest}
    />
  );
};

export const FormListItemPictureAutocomplete = connect(mapStateToProps, ownActions)(FormListItemPictureAutocompleteBase);
